import React, { useState } from 'react';
import { useTranslation } from '@shared/core';

import type { FilterFacet } from '../Filter.types';
import { useDesignGalleryFiltersContext } from '../../../DesignGalleryFiltersProvider';
import { PillTrigger as Trigger } from './FilterShared';
import { summarizeFacetSelection } from '../selectionSummary';
import { CYCLING_LABEL_INTERVAL_MS, useCyclingLabel } from '../useCyclingLabel';
import { useWidestLabelWidth } from '../useWidestLabelWidth';
import { FEATURE_FACET } from '../Filters.constants';

const FEATURE_CYCLE_TRIPS = 2;

interface FilterPillProps {
  facet: FilterFacet;
  /** Opens this facet's rail, or the drawer on mobile. */
  onTrigger: (groupCode: string) => void;
  /** Whether this facet's rail is the open one; drives the chevron and `aria-expanded`. */
  isRailOpen?: boolean;
  /** `id` of the rail the pill controls, for `aria-controls`. */
  railId?: string;
  pillHeight?: number;
}

export const FilterPill = React.memo((props: FilterPillProps) => {
  const { facet, onTrigger, pillHeight, isRailOpen = false, railId } = props;
  const { options } = facet;
  const { activePrintFilters } = useDesignGalleryFiltersContext();
  const { tq } = useTranslation('stationery');
  // The tail for a selection too long to name in full: "Art Deco, Beach, +3" — "+N"
  // rather than "N more" to save width. `tq` has to sit lexically in the component — the
  // extractor cannot trace it through a function parameter — so the pure summariser takes it
  // as a formatter.
  const formatMore = (omitted: number) => tq`+${omitted}`;
  // An active pill names its selection ("Magnet", "Blue, Black") and fills
  // dark, eliding the overflow rather than changing its look (Jeff, 2026-10-23).
  const selection = summarizeFacetSelection(facet, activePrintFilters, formatMore);
  const namesSelection = selection.mode === 'names';
  // The accessible name lists every selected option, so a screen reader never hears "plus 2".
  const selectedNames = options.filter(option => activePrintFilters.has(option.compositeCode)).map(option => option.label);

  // Idle, the Feature pill cycles "Feature", then its bare option names ("Free", "Photo", …),
  // twice through before resting back on "Feature", to advertise them. Opening its rail stops
  // the cycling outright; hovering or focusing the pill only freezes it, so the label a reader is part-way through holds still rather than
  // snapping back (WCAG 2.2.2). The hook stands the whole thing down under reduced motion.
  // Hover and focus are tracked apart: sharing one flag would resume cycling when the pointer
  // leaves a pill that still holds keyboard focus.
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const cycledLabels = [facet.label, ...options.map(option => option.label)];
  const canCycle = facet.groupCode === FEATURE_FACET && selection.mode === 'none';
  const isCycling = canCycle && !isRailOpen;
  const cyclingLabel = useCyclingLabel(cycledLabels, isCycling, isHovered || isFocused, CYCLING_LABEL_INTERVAL_MS, FEATURE_CYCLE_TRIPS);

  // Reserve the width of the widest label so cycling never moves the pill's own edges — see
  // `useWidestLabelWidth` for the flicker that a resizing pill caused. Keyed on `canCycle`, not
  // `isCycling`, so opening the rail (which stops the cycle) does not shrink the pill either.
  const [labelElement, setLabelElement] = useState<HTMLElement | null>(null);
  const reservedWidth = useWidestLabelWidth(canCycle ? cycledLabels : [], labelElement);

  return (
    <Trigger
      isOpen={isRailOpen}
      label={
        <span
          ref={setLabelElement}
          style={reservedWidth ? { display: 'inline-block', minWidth: reservedWidth, textAlign: 'left' } : undefined}
        >
          {cyclingLabel ?? selection.label}
        </span>
      }
      color={namesSelection ? 'white' : undefined}
      backgroundColor={namesSelection ? (open: boolean) => (open ? ('mono13' as const) : ('mono14' as const)) : undefined}
      onClick={() => onTrigger(facet.groupCode)}
      pillHeight={pillHeight}
      ariaExpanded={isRailOpen}
      // A bare cycled option ("Free") would drop the group from the button's name, and a name
      // that changes every few seconds is noise; the group name is the stable one. A selection
      // label drops the group and elides names visually, so restore both for the accessible name.
      ariaLabel={isCycling ? facet.label : namesSelection ? `${facet.label}: ${selectedNames.join(', ')}` : undefined}
      ariaControls={railId}
      onHoverChange={setIsHovered}
      onFocusChange={setIsFocused}
    />
  );
});
